type SentryModule = typeof import("@sentry/react");

let sentryModulePromise: Promise<SentryModule> | null = null;
const PLACEHOLDER_PREFIXES = ["your-", "replace-", "example-"];
const SENTRY_DSN_FALLBACK = "https://e284a5b071cc172627335c962c65255b@o4510237227876352.ingest.us.sentry.io/4511235272212480";
let sentryInitialized = false;

function loadSentry() {
  if (!sentryModulePromise) {
    sentryModulePromise = import("@sentry/react");
  }

  return sentryModulePromise;
}

function getConfiguredSentryDsn() {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
    || import.meta.env.NEXT_PUBLIC_SENTRY_DSN?.trim()
    || SENTRY_DSN_FALLBACK;

  if (!dsn || PLACEHOLDER_PREFIXES.some((prefix) => dsn.toLowerCase().startsWith(prefix))) {
    return null;
  }

  return dsn;
}

function getSentryRelease() {
  return import.meta.env.VITE_SENTRY_RELEASE?.trim()
    || import.meta.env.NEXT_PUBLIC_SENTRY_RELEASE?.trim()
    || import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA?.trim()
    || import.meta.env.VERCEL_GIT_COMMIT_SHA?.trim()
    || undefined;
}

function normalizeCapturedError(error: unknown) {
  if (error instanceof Error) {
    return error;
  }

  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    const messageParts = [
      typeof record.message === "string" ? record.message : null,
      typeof record.code === "string" ? `code=${record.code}` : null,
      typeof record.hint === "string" ? `hint=${record.hint}` : null,
      typeof record.details === "string" ? `details=${record.details}` : null,
    ].filter(Boolean);

    const normalized = new Error(
      messageParts.length > 0
        ? messageParts.join(" | ")
        : "Unexpected non-Error exception captured",
    );
    (normalized as Error & { cause?: unknown }).cause = error;
    return normalized;
  }

  return new Error(typeof error === "string" ? error : "Unknown error");
}

export async function initializeSentry() {
  const dsn = getConfiguredSentryDsn();

  if (!dsn || typeof window === "undefined" || sentryInitialized) {
    return;
  }

  const [{ supabase }, Sentry] = await Promise.all([
    import("@/integrations/supabase/client"),
    loadSentry(),
  ]);

  const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.2");
  const replaysSessionSampleRate = Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? "0.05");
  const replaysOnErrorSampleRate = Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? "1");
  sentryInitialized = true;

  Sentry.init({
    dsn,
    sendDefaultPii: true,
    enabled: true,
    debug: import.meta.env.DEV,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT
      ?? import.meta.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT
      ?? import.meta.env.MODE,
    release: getSentryRelease(),
    dist: import.meta.env.VITE_SENTRY_DIST?.trim()
      || import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA?.trim()?.slice(0, 12)
      || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.supabaseIntegration(supabase),
    ],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.2,
    replaysSessionSampleRate: Number.isFinite(replaysSessionSampleRate) ? replaysSessionSampleRate : 0.05,
    replaysOnErrorSampleRate: Number.isFinite(replaysOnErrorSampleRate) ? replaysOnErrorSampleRate : 1,
    tracePropagationTargets: [
      window.location.origin,
      /https:\/\/.*\.supabase\.co/,
    ],
    beforeSend(event) {
      event.tags = {
        ...event.tags,
        app: "proptrust-navigator",
        runtime: "browser",
      };
      return event;
    },
  });
}

export function initializeSentryWhenIdle() {
  if (typeof window === "undefined" || !getConfiguredSentryDsn()) {
    return;
  }

  const start = () => {
    void initializeSentry();
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(start, { timeout: 2000 });
  } else {
    window.setTimeout(start, 0);
  }
}

export function captureSentryException(error: unknown, context?: Record<string, unknown>) {
  if (!getConfiguredSentryDsn()) {
    return;
  }

  void loadSentry().then((Sentry) => {
    Sentry.captureException(normalizeCapturedError(error), context);
  });
}

export function setSentryUser(
  user: {
    id?: string | null;
    email?: string | null;
    username?: string | null;
  } | null,
) {
  if (!getConfiguredSentryDsn()) {
    return;
  }

  void loadSentry().then((Sentry) => {
    Sentry.setUser(user);
  });
}
